/** Site activation ramp: actual active sites (step area) vs contracted plan (step line). */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  useD3Chart,
  drawTimeAxes,
  drawCutoff,
  parseMonth,
  FONT,
  type Margin,
} from "./chartBase";
import type { SiteActivationRampProps } from "./onboardingPlots.types";

const CONTRACTED_COLOR = "#C9922B";

export default function SiteActivationRamp({
  rows,
  height = 300,
}: SiteActivationRampProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      if (!rows.length) return;
      const H = height;
      const m: Margin = { top: 16, right: 16, bottom: 34, left: 36 };
      const pts = rows.map((r) => ({ ...r, _d: parseMonth(r.date) as Date }));

      const x = d3
        .scaleTime()
        .domain(d3.extent(pts, (r) => r._d) as [Date, Date])
        .range([m.left, W - m.right]);
      const y = d3
        .scaleLinear()
        .domain([
          0,
          d3.max(pts, (r) =>
            Math.max(r.contractedActiveSites ?? 0, r.totalActiveSites ?? 0),
          ) || 1,
        ])
        .nice()
        .range([H - m.bottom, m.top]);
      drawTimeAxes(svg, W, H, m, x, y, undefined, true);

      svg
        .append("path")
        .datum(pts)
        .attr("fill", "none")
        .attr("stroke", CONTRACTED_COLOR)
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "6 4")
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr(
          "d",
          d3
            .line<any>()
            .curve(d3.curveStepAfter)
            .defined((r) => r.contractedActiveSites != null)
            .x((r) => x(r._d))
            .y((r) => y(r.contractedActiveSites as number)),
        );
      svg
        .append("path")
        .datum(pts)
        .attr("fill", COLORS.actual)
        .attr("opacity", 0.12)
        .attr(
          "d",
          d3
            .area<any>()
            .curve(d3.curveStepAfter)
            .defined((r) => r.totalActiveSites != null)
            .x((r) => x(r._d))
            .y0(y(0))
            .y1((r) => y(r.totalActiveSites as number)),
        );
      svg
        .append("path")
        .datum(pts)
        .attr("fill", "none")
        .attr("stroke", COLORS.actual)
        .attr("stroke-width", 2.6)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr(
          "d",
          d3
            .line<any>()
            .curve(d3.curveStepAfter)
            .defined((r) => r.totalActiveSites != null)
            .x((r) => x(r._d))
            .y((r) => y(r.totalActiveSites as number)),
        );

      const cutoff = [...pts].reverse().find((r) => r.totalActiveSites != null);
      if (cutoff) drawCutoff(svg, cutoff._d, m, H, x);

      svg
        .selectAll("circle")
        .data(pts.filter((r) => r.totalActiveSites != null))
        .join("circle")
        .attr("cx", (r: any) => x(r._d))
        .attr("cy", (r: any) => y(r.totalActiveSites as number))
        .attr("r", 3)
        .attr("fill", COLORS.actual)
        .on("mousemove", (ev: MouseEvent, r: any) =>
          tip.show(
            `<b>${r.date}</b><br>Active: <b>${r.totalActiveSites}</b><br>Contracted: ${r.contractedActiveSites}`,
            ev,
          ),
        )
        .on("mouseleave", () => tip.hide());
    },
    [rows, height],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
